import { isGbpDisplayCurrency } from '@/lib/currency/resolveDisplayCurrency'
import {
  getDefaultVATPreference,
  type VATPreference,
} from '@/vat/vatUtils'

const STANDARD_UK_VAT_MULTIPLIER = 1.2

type SearchPriceSource = {
  priceUsd?: number | null
  discountUsd?: number | null
  priceGbp?: number | null
  discountGbp?: number | null
}

type ResolveSearchDisplayPriceArgs = {
  result: SearchPriceSource
  currencyCode: string | null | undefined
  countryIsoCode?: string | null
  vatPreference?: VATPreference
}

const roundCurrency = (amount: number) => Math.round(amount * 100) / 100

const validAmount = (amount: number | null | undefined) =>
  typeof amount === 'number' && Number.isFinite(amount) ? amount : null

const applyStandardVatFallback = (amount: number | null) =>
  amount === null ? null : roundCurrency(amount * STANDARD_UK_VAT_MULTIPLIER)

export function resolveSearchDisplayPrice({
  result,
  currencyCode,
  countryIsoCode,
  vatPreference,
}: ResolveSearchDisplayPriceArgs) {
  const isGbp = isGbpDisplayCurrency(currencyCode)
  const rawPrice = validAmount(isGbp ? result.priceGbp : result.priceUsd)
  const rawSalePrice = validAmount(
    isGbp ? result.discountGbp : result.discountUsd
  )
  const effectiveVatPreference =
    vatPreference ?? getDefaultVATPreference(countryIsoCode)

  // Typesense search prices currently expose GBP ex-VAT values only.
  const shouldApplyVatFallback =
    isGbp && effectiveVatPreference === 'inc' && rawPrice !== null

  const price = shouldApplyVatFallback
    ? applyStandardVatFallback(rawPrice)
    : rawPrice
  const salePriceCandidate = shouldApplyVatFallback
    ? applyStandardVatFallback(rawSalePrice)
    : rawSalePrice
  const hasDiscount =
    price !== null &&
    salePriceCandidate !== null &&
    salePriceCandidate > 0 &&
    salePriceCandidate < price

  return {
    price,
    salePrice: hasDiscount ? salePriceCandidate : null,
    hasDiscount,
  }
}
